import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

const HIGH_VALUE_THRESHOLD = 999900;

/**
 * Keeps the purchase controls inside the quick-add modal in sync with the
 * metafield-aware variant price map.
 * @param {ParentNode} root
 * @param {Record<string, any>} [detail]
 */
function syncQuickAddEnquiry(root, detail = {}) {
  const container = /** @type {HTMLElement | null} */ (
    root.querySelector('[data-amada-buy-buttons-container]')
  );
  if (!container) return;

  const variantId = String(
    detail.resource?.id ??
      detail.expectedVariantId ??
      detail.variantId ??
      root.querySelector('product-form-component input[name="id"]')?.value ??
      ''
  );

  let priceInfo;
  if (variantId) {
    try {
      const priceMap = JSON.parse(root.querySelector('script[data-product-variants]')?.textContent || '{}');
      priceInfo = priceMap.prices?.[variantId];
    } catch {
      // The server-rendered container below remains the fallback.
    }
  }

  const renderedContainer =
    detail.html?.querySelector?.('[data-amada-buy-buttons-container]') ?? container;
  const renderedPrice = Number(detail.resource?.price ?? renderedContainer.dataset.variantPrice ?? 0);
  const isHighValue = priceInfo
    ? Boolean(
        priceInfo.isHighValue ||
          priceInfo.hasMetafield ||
          Number(priceInfo.cents) >= HIGH_VALUE_THRESHOLD
      )
    : Boolean(
        renderedContainer.classList.contains('is-high-value') ||
          renderedContainer.dataset.metafieldPrice ||
          renderedPrice >= HIGH_VALUE_THRESHOLD
      );

  const addToCart = /** @type {HTMLElement | null} */ (container.querySelector('[data-amada-atc-wrapper]'));
  const enquiry = /** @type {HTMLElement | null} */ (container.querySelector('[data-amada-enquiry-wrapper]'));

  container.classList.toggle('is-high-value', isHighValue);
  addToCart?.classList.toggle('hidden', isHighValue);
  addToCart?.style.setProperty('display', isHighValue ? 'none' : '', isHighValue ? 'important' : '');
  enquiry?.classList.toggle('hidden', !isHighValue);
  enquiry?.style.setProperty('display', isHighValue ? 'block' : 'none', 'important');
}

const productPageEnquirySync = window.amadaSyncHighValueEnquiry;
window.amadaSyncHighValueEnquiry = (detail) => {
  const quickAddDialog = document.getElementById('quick-add-dialog');
  if (
    quickAddDialog?.querySelector('dialog[open]') &&
    quickAddDialog.querySelector('[data-amada-buy-buttons-container]')
  ) {
    syncQuickAddVariantId(quickAddDialog, detail);
    syncQuickAddEnquiry(quickAddDialog, detail);
    return;
  }
  productPageEnquirySync?.(detail);
};

function createAssistanceElement(productTitle = '', productUrl = '') {
  const el = document.createElement('div');
  el.className = 'amada-quick-add-assistance';
  const titleText = productTitle || 'this piece';
  const urlText = productUrl || window.location.href;
  const waMsg = encodeURIComponent(`Hi Amada Diamonds, I am interested in ${titleText}. Can you help me with customisation or other options? ${urlText}`);
  const mailSubject = encodeURIComponent(`Customisation enquiry: ${titleText}`);
  const mailBody = encodeURIComponent(`Hi Amada Diamonds,\n\nI am interested in ${titleText} (${urlText}) and would like to know more about customisation or different preferences.\n\nThank you.`);

  el.innerHTML = `
    <div class="amada-quick-add-assistance__info">
      <div class="amada-quick-add-assistance__title">Looking for different options ?</div>
      <div class="amada-quick-add-assistance__subtitle">We can help you with customisation or any other preference</div>
    </div>
    <div class="amada-quick-add-assistance__actions">
      <a href="https://wa.me/442080894040?text=${waMsg}" target="_blank" rel="noopener" class="amada-quick-add-assistance__btn" aria-label="WhatsApp Us" title="WhatsApp Us">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
          <path d="M12.04 2C6.5 2 2.004 6.486 2.004 12.014c0 1.77.463 3.495 1.341 5.012L2 22l5.09-1.331A10.01 10.01 0 0 0 12.04 22C17.58 22 22.076 17.514 22.076 11.986 22.076 6.458 17.58 2 12.04 2zm0 18.14c-1.64 0-3.25-.44-4.66-1.27l-.33-.2-3.02.79.81-2.94-.22-.34A8.13 8.13 0 0 1 3.86 12.01c0-4.51 3.67-8.18 8.18-8.18 4.51 0 8.18 3.67 8.18 8.18 0 4.51-3.67 8.18-8.18 8.18z"/>
        </svg>
      </a>
      <a href="mailto:enquiries@amadadiamonds.com?subject=${mailSubject}&body=${mailBody}" class="amada-quick-add-assistance__btn" aria-label="Email Us" title="Email Us">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2"></rect>
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
        </svg>
      </a>
      <a href="tel:+442080894040" class="amada-quick-add-assistance__btn" aria-label="Call Us" title="Call Us">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
        </svg>
      </a>
    </div>
  `;
  return el;
}

function keepPrimaryVariantIdInput(container) {
  if (!container) return;

  const form = container.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  if (!form) return;

  const primary =
    form.querySelector('input[name="id"][ref="variantId"]') || form.querySelector('input[name="id"]');
  if (!primary) return;

  form.querySelectorAll('input[name="id"]').forEach((input) => {
    if (input !== primary) input.remove();
  });
}

function syncQuickAddVariantId(root, detail = {}) {
  if (!root) return;

  keepPrimaryVariantIdInput(root);

  const form = root.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  const input =
    form?.querySelector('input[name="id"][ref="variantId"]') || form?.querySelector('input[name="id"]');
  if (!input) return;

  const nextId = detail.expectedVariantId ?? detail.resource?.id ?? detail.variantId;
  if (nextId == null || nextId === '') return;

  input.value = String(nextId);
}

function stripUnwantedQuickAddElements(container) {
  if (!container) return;
  const selectors = [
    '.amada-aside',
    '[class*="amada-aside"]',
    '.amada-wishlist-trigger-btn',
    '[data-amada-wishlist-trigger]',
    '.amada-wishlist-modal',
    '.amada-wishlist-hulk-carrier',
    '[data-block-id*="amada_product_aside"]',
    'amada-product-aside',
    '.amada-product-aside',
    '[class*="make-an-offer"]',
    '.view-product-title',
    'product-sku-component',
    '.rating-wrapper',
    '.installments',
    '.group-block__media-wrapper',
    'product-inventory',
    'quantity-selector-component',
    '.quantity-selector-wrapper',
    '.quantity-rules',
    '.quantity-label',
    '.volume-pricing',
    '.amada-product-eyebrow-container',
    '.amada-igi-info-trigger',
    'dialog.amada-popup-dialog',
    '.amada-popup-dialog',
  ];
  container.querySelectorAll(selectors.join(', ')).forEach((el) => el.remove());

  container.querySelectorAll('.shopify-block, .group-block').forEach((el) => {
    if (el.querySelector('.amada-product-title, .amada-product-title-wrap, product-price, variant-picker, product-form-component, .buy-buttons-block, .amada-quick-add-assistance, product-custom-property-component, [data-amada-personalise]')) {
      return;
    }

    if (!el.textContent.trim() && !el.querySelector('img, button, input, a, svg')) {
      el.remove();
      return;
    }

    const visibleChild = [...el.children].some((child) => {
      if (!(child instanceof HTMLElement)) return false;
      const style = window.getComputedStyle(child);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });

    if (!visibleChild) el.remove();
  });

  keepPrimaryVariantIdInput(container);
}

function bindQuickAddLineItemFields(container) {
  if (!container) return;

  const form =
    container.querySelector('product-form-component form[data-type="add-to-cart-form"]') ||
    container.querySelector('form[data-type="add-to-cart-form"]');
  if (!form) return;

  // Not `form.id`: the form holds an <input name="id">, which shadows the property.
  let formId = form.getAttribute('id');
  if (!formId) {
    formId = `QuickAdd-ProductForm-${Date.now()}`;
    form.setAttribute('id', formId);
  }

  container
    .querySelectorAll(
      'product-custom-property-component input, product-custom-property-component textarea, [data-amada-personalise] input, [data-amada-personalise] textarea, input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
    )
    .forEach((input) => {
      input.setAttribute('form', formId);
    });

  document.dispatchEvent(new Event('quick-add:ready'));

  const host = container.closest('quick-add-dialog') || container;
  host.querySelectorAll(':scope > amada-ring-size-gate').forEach((el) => el.remove());

  const needsGate =
    container.querySelector('[data-amada-ring-size]') ||
    [...container.querySelectorAll('variant-picker fieldset')].some((fieldset) =>
      /ring\s*size|^\s*size\s*$/i.test(fieldset.querySelector('legend')?.textContent?.trim() || '')
    );
  if (!needsGate) return;

  const sourceGate = document.querySelector('section amada-ring-size-gate, .shopify-section amada-ring-size-gate');
  const gate = document.createElement('amada-ring-size-gate');
  if (sourceGate) {
    ['chooseLabel', 'addLabel', 'unknownLabel', 'unknownHelp', 'unknownFallbackUrl', 'bookLabel', 'bookUrl'].forEach(
      (key) => {
        const value = sourceGate.dataset[key];
        if (value) gate.dataset[key] = value;
      }
    );
  }

  host.appendChild(gate);
}

function jewelryPropertiesFromForm(formData) {
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[([^\]]+)\]$/);
    if (!match || value == null) continue;
    const text = String(value).trim();
    if (text) properties[match[1]] = text;
  }
  return properties;
}

/**
 * Collect line-item properties the same way Add to Cart does:
 * FormData + properties[*] controls in product UI (engraving often outside <form>).
 * @param {HTMLFormElement} form
 * @param {ParentNode} [scope]
 */
function collectBuyNowProperties(form, scope) {
  /** @type {Record<string, string>} */
  const properties = jewelryPropertiesFromForm(new FormData(form));
  const root = scope || form;
  const formId = form.getAttribute('id') || '';

  const applyControl = (el) => {
    if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) {
      return;
    }
    if (el.disabled) return;
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio') && !el.checked) {
      return;
    }
    const name = el.getAttribute('name') || '';
    const match = name.match(/^properties\[([^\]]+)\]$/);
    if (!match) return;

    // Prefer this form; accept same-scope fields when form= is stale after quick-add morph.
    if (el.form && el.form !== form) return;
    const formAttr = el.getAttribute('form');
    if (!el.form && formAttr && formId && formAttr !== formId) {
      if (!(root instanceof Node) || !root.contains(el)) return;
    }

    const text = String(el.value || '').trim();
    if (text) properties[match[1]] = text;
  };

  if (root instanceof Element || root instanceof Document) {
    root
      .querySelectorAll(
        'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
      )
      .forEach(applyControl);
  }

  // Live engraving value (field can sit outside <form> / lose form association).
  const personaliseHost =
    (root instanceof Element ? root.querySelector('[data-amada-personalise]') : null) ||
    form.closest('quick-add-dialog, dialog, .quick-add-modal, .shopify-section, product-form-component')
      ?.querySelector('[data-amada-personalise]') ||
    document.querySelector('[data-amada-personalise]');

  /** @type {(Element | null | undefined)[]} */
  const engravingCandidates = [];
  if (personaliseHost) engravingCandidates.push(personaliseHost.querySelector('input, textarea'));
  if (root instanceof Element || root instanceof Document) {
    root
      .querySelectorAll(
        'product-custom-property-component input, product-custom-property-component textarea, input[name="properties[Engraving]"], textarea[name="properties[Engraving]"]'
      )
      .forEach((el) => engravingCandidates.push(el));
  }

  for (const engravingEl of engravingCandidates) {
    if (!engravingEl) continue;
    const engravingText = String(/** @type {HTMLInputElement | HTMLTextAreaElement} */ (engravingEl).value || '').trim();
    if (!engravingText) continue;
    const host =
      (engravingEl instanceof Element ? engravingEl.closest('[data-amada-personalise]') : null) ||
      personaliseHost;
    if (host?.dataset?.engravingConfirmed !== 'true') {
      for (const key of Object.keys(properties)) {
        if (String(key).toLowerCase().includes('engrav')) delete properties[key];
      }
      continue;
    }
    const named = engravingEl.getAttribute('name')?.match(/^properties\[([^\]]+)\]$/);
    const key = named?.[1] || 'Engraving';
    properties[key] = engravingText;
    break;
  }

  return properties;
}

/**
 * @param {Element | null | undefined} personalise
 * @returns {Promise<string>}
 */
async function resolveEngravingFeeVariantId(personalise) {
  const fromDom = personalise?.dataset?.feeVariantId || '';
  if (fromDom) return fromDom;

  try {
    const res = await fetch('/products/amada-hand-engraving.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return '';
    const product = await res.json();
    const variant =
      product?.variants?.find(/** @param {{available?: boolean}} v */ (v) => v?.available) || product?.variants?.[0];
    return variant?.id ? String(variant.id) : '';
  } catch {
    return '';
  }
}

/**
 * Buy now: clear cart, add only this variant (+ line properties / engraving fee), checkout.
 * Mirrors Add to Cart payload (Ring Size, Engraving, other properties, fee line).
 * Never falls back to /cart/{id}:1 because that drops line-item properties.
 * @param {HTMLFormElement} form
 * @param {string} variantId
 */
async function checkoutBuyNowOnly(form, variantId) {
  const formData = new FormData(form);
  formData.set('id', variantId);
  if (!formData.get('quantity')) formData.set('quantity', '1');

  const quantity = Number(formData.get('quantity') || 1) || 1;
  const scope =
    form.closest('quick-add-dialog, dialog, .quick-add-modal, product-form-component, .shopify-section') || form;
  const properties = collectBuyNowProperties(form, scope);

  /** @type {{ id: number, quantity: number, properties?: Record<string, string> }[]} */
  const items = [
    {
      id: Number(variantId),
      quantity,
      ...(Object.keys(properties).length ? { properties } : {}),
    },
  ];

  const personalise =
    (scope instanceof Element ? scope.querySelector('[data-amada-personalise]') : null) ||
    form.querySelector('[data-amada-personalise]') ||
    document.querySelector('[data-amada-personalise]');

  const engraving =
    properties.Engraving ||
    properties.engraving ||
    Object.entries(properties).find(([k]) => String(k).toLowerCase() === 'engraving')?.[1] ||
    '';

  if (engraving) {
    const feeVariantId = await resolveEngravingFeeVariantId(personalise);
    if (feeVariantId) {
      items.push({
        id: Number(feeVariantId),
        quantity,
        properties: { _for: String(variantId) },
      });
    }
  }

  const jsonHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  const clearRes = await fetch(`${(typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart'}/clear.js`, {
    method: 'POST',
    headers: jsonHeaders,
    credentials: 'same-origin',
  });
  if (!clearRes.ok) throw new Error('Could not clear cart for Buy now');

  const addRes = await fetch((typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || '/cart/add.js', {
    method: 'POST',
    headers: jsonHeaders,
    credentials: 'same-origin',
    body: JSON.stringify({ items }),
  });
  if (!addRes.ok) throw new Error('Could not add item for Buy now');

  const addJson = await addRes.json().catch(() => null);
  if (addJson?.status) {
    throw new Error(addJson.description || addJson.message || 'Could not add item for Buy now');
  }

  window.location.href = '/checkout';
}

function attachQuickAddBuyNowButton(container) {
  const formButtons = container.querySelector('.product-form-buttons');
  if (!formButtons) return;

  const atcBtn = formButtons.querySelector('button[name="add"]');
  if (!atcBtn) return;

  atcBtn.classList.add('quick-add-atc-btn');

  const atcWrapper = atcBtn.closest('add-to-cart-component') || atcBtn;
  const atcHost = atcWrapper.closest('span') || atcWrapper.parentElement;
  if (!atcHost) return;

  let buyNowBtn = formButtons.querySelector('.quick-add-buy-now-btn');
  if (!buyNowBtn) {
    buyNowBtn = document.createElement('button');
    buyNowBtn.type = 'button';
    buyNowBtn.className = 'quick-add-buy-now-btn button button-secondary';
    buyNowBtn.textContent = 'Buy now';
  }

  if (buyNowBtn.parentElement !== atcHost) {
    atcHost.appendChild(buyNowBtn);
  }

  // Keep label as "Buy now" always. Only mirror ATC disabled/aria-disabled state.
  const syncBuyNowWithAtc = () => {
    if (buyNowBtn.getAttribute('aria-busy') === 'true') return;
    const gated = atcBtn.disabled || atcBtn.getAttribute('aria-disabled') === 'true';
    buyNowBtn.disabled = false;
    buyNowBtn.textContent = 'Buy now';
    if (gated) {
      buyNowBtn.setAttribute('aria-disabled', 'true');
      buyNowBtn.setAttribute('data-amada-needs-size', 'true');
    } else {
      buyNowBtn.removeAttribute('aria-disabled');
      buyNowBtn.removeAttribute('data-amada-needs-size');
    }
  };

  if (!buyNowBtn.dataset.amadaBuyNowBound) {
    syncBuyNowWithAtc();
    const atcObserver = new MutationObserver(syncBuyNowWithAtc);
    atcObserver.observe(atcBtn, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    const atcLabelEl = atcBtn.querySelector('[data-add-to-cart-label]');
    if (atcLabelEl) {
      atcObserver.observe(atcLabelEl, { childList: true, characterData: true, subtree: true });
    }

    buyNowBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Size still required — ring-size gate intercepts via data-amada-needs-size.
      if (atcBtn.disabled || atcBtn.getAttribute('aria-disabled') === 'true') {
        if (buyNowBtn.getAttribute('data-amada-needs-size') === 'true') return;
        atcBtn.click();
        return;
      }

      const form = formButtons.closest('form');
      if (!form) return;

      keepPrimaryVariantIdInput(container);

      const variantInput =
        form.querySelector('input[name="id"][ref="variantId"]') || form.querySelector('input[name="id"]');
      const variantId = variantInput?.value;
      if (!variantId) return;

      buyNowBtn.textContent = 'Processing...';
      buyNowBtn.setAttribute('aria-busy', 'true');
      buyNowBtn.setAttribute('aria-disabled', 'true');

      checkoutBuyNowOnly(form, variantId).catch(() => {
        buyNowBtn.textContent = 'Buy now';
        buyNowBtn.removeAttribute('aria-busy');
        syncBuyNowWithAtc();
      });
    });

    buyNowBtn.dataset.amadaBuyNowBound = 'true';
  } else {
    syncBuyNowWithAtc();
  }
}

function attachQuickAddAssistance(container, productTitleText = '', productUrl = '') {
  container.querySelectorAll('.amada-quick-add-assistance').forEach((el) => el.remove());

  const assistanceEl = createAssistanceElement(productTitleText, productUrl);
  const details = container.querySelector('.product-details');
  const content = details?.querySelector('.group-block-content');

  if (content) {
    content.appendChild(assistanceEl);
    return;
  }

  if (details) {
    details.appendChild(assistanceEl);
    return;
  }

  const target =
    container.querySelector('.amada-atc-buttons-wrapper') ||
    container.querySelector('product-form-component') ||
    container.querySelector('.buy-buttons-block') ||
    container.querySelector('.product-form-buttons');

  if (target) {
    target.insertAdjacentElement('afterend', assistanceEl);
  } else {
    container.appendChild(assistanceEl);
  }
}

function buildQuickAddThumbnails(productGrid) {
  const slideshow = productGrid.querySelector('slideshow-component');
  if (!slideshow) return;

  slideshow.querySelector('.quick-add-thumbnail-nav')?.remove();

  const defaultControls = slideshow.querySelector('slideshow-controls');
  if (defaultControls) {
    defaultControls.style.display = 'none';
  }

  const slides = [...slideshow.querySelectorAll('slideshow-slide')];
  if (slides.length <= 1) return;

  const nav = document.createElement('div');
  nav.className = 'quick-add-thumbnail-nav';

  const prevBtn = document.createElement('button');
  prevBtn.type = 'button';
  prevBtn.className = 'quick-add-thumb-arrow quick-add-thumb-arrow--prev';
  prevBtn.setAttribute('aria-label', 'Previous image');
  prevBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';

  const nextBtn = document.createElement('button');
  nextBtn.type = 'button';
  nextBtn.className = 'quick-add-thumb-arrow quick-add-thumb-arrow--next';
  nextBtn.setAttribute('aria-label', 'Next image');
  nextBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';

  const track = document.createElement('div');
  track.className = 'quick-add-thumbnails-track';

  slides.forEach((slide, idx) => {
    const img = slide.querySelector('img');
    if (!img) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'quick-add-thumb-btn';
    if (idx === 0) btn.classList.add('is-active');
    btn.dataset.slideIndex = String(idx);
    const mediaId = slide.dataset.mediaId || slide.getAttribute('data-media-id') || slide.getAttribute('slide-id') || '';
    if (mediaId) btn.dataset.mediaId = mediaId;
    btn.setAttribute('aria-label', `View image ${idx + 1}`);

    const thumbImg = document.createElement('img');
    thumbImg.src = img.getAttribute('src') || img.src || '';
    thumbImg.alt = img.alt || '';
    thumbImg.loading = 'lazy';
    btn.appendChild(thumbImg);

    track.appendChild(btn);
  });

  nav.appendChild(prevBtn);
  nav.appendChild(track);
  nav.appendChild(nextBtn);

  slideshow.appendChild(nav);
}

function highlightActiveThumbnail(modalContent, targetIndex = null) {
  const slideshow = modalContent.querySelector('slideshow-component');
  const nav = modalContent.querySelector('.quick-add-thumbnail-nav');
  if (!slideshow || !nav) return;

  const thumbBtns = [...nav.querySelectorAll('.quick-add-thumb-btn')];
  if (!thumbBtns.length) return;

  const allSlides = [...slideshow.querySelectorAll('slideshow-slide')];

  let activeIdx = targetIndex;
  if (activeIdx === null || activeIdx === undefined) {
    const currentSlide = slideshow.currentSlide || slideshow.slides?.[slideshow.current];
    if (currentSlide) {
      activeIdx = allSlides.indexOf(currentSlide);
    }
    if (activeIdx < 0 || activeIdx === null || activeIdx === undefined) {
      const visibleIdx = allSlides.findIndex((s) => !s.hasAttribute('hidden') && s.style.display !== 'none');
      activeIdx = visibleIdx >= 0 ? visibleIdx : (slideshow.current ?? 0);
    }
  }

  thumbBtns.forEach((btn, idx) => {
    // CRITICAL: NEVER hide or remove any thumbnails across variant changes
    btn.style.display = 'flex';
    const isActive = idx === Number(activeIdx);
    btn.classList.toggle('is-active', isActive);
    if (isActive) {
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  });
}

function initQuickAddThumbnailInteractions(modalContent) {
  const slideshow = modalContent.querySelector('slideshow-component');
  const nav = modalContent.querySelector('.quick-add-thumbnail-nav');
  if (!slideshow || !nav) return;

  const track = nav.querySelector('.quick-add-thumbnails-track');
  const prevBtn = nav.querySelector('.quick-add-thumb-arrow--prev');
  const nextBtn = nav.querySelector('.quick-add-thumb-arrow--next');

  slideshow.addEventListener('slideshow:select', (e) => {
    const allSlides = [...slideshow.querySelectorAll('slideshow-slide')];
    if (e.detail?.slide) {
      const idx = allSlides.indexOf(e.detail.slide);
      if (idx >= 0) {
        highlightActiveThumbnail(modalContent, idx);
        return;
      }
    }
    if (e.detail?.index !== undefined) {
      const visibleSlides = slideshow.slides || [];
      const targetSlide = visibleSlides[e.detail.index];
      const allIdx = targetSlide ? allSlides.indexOf(targetSlide) : e.detail.index;
      highlightActiveThumbnail(modalContent, allIdx >= 0 ? allIdx : e.detail.index);
    }
  });

  track?.addEventListener('click', (e) => {
    const btn = e.target.closest('.quick-add-thumb-btn');
    if (!btn) return;
    const idx = Number(btn.dataset.slideIndex);
    if (isNaN(idx)) return;

    const allSlides = [...slideshow.querySelectorAll('slideshow-slide')];
    const targetSlide = allSlides[idx];
    if (targetSlide) {
      targetSlide.removeAttribute('hidden');
      targetSlide.removeAttribute('aria-hidden');
      targetSlide.style.display = '';
    }

    if (typeof slideshow.select === 'function') {
      const visibleSlides = slideshow.slides || [];
      const visibleIndex = visibleSlides.indexOf(targetSlide);
      if (visibleIndex >= 0) {
        slideshow.select(visibleIndex);
      } else {
        try {
          slideshow.select(idx);
        } catch {
          targetSlide?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    } else if (targetSlide) {
      targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    highlightActiveThumbnail(modalContent, idx);
  });

  prevBtn?.addEventListener('click', () => {
    if (typeof slideshow.previous === 'function') {
      slideshow.previous();
    } else if (typeof slideshow.select === 'function') {
      const cur = slideshow.current ?? 0;
      slideshow.select(Math.max(0, cur - 1));
    }
  });

  nextBtn?.addEventListener('click', () => {
    if (typeof slideshow.next === 'function') {
      slideshow.next();
    } else if (typeof slideshow.select === 'function') {
      const cur = slideshow.current ?? 0;
      slideshow.select(cur + 1);
    }
  });
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const productTitleText =
      this.dataset.productTitle ||
      productGrid.querySelector('.amada-product-title')?.textContent?.trim() ||
      productGrid.querySelector('.view-product-title a')?.textContent?.trim() ||
      '';
    const productUrl = this.productPageUrl || window.location.href;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productMedia = productGrid.querySelector('.product-information__media');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const ringSizeProperty = productGrid.querySelector('.amada-ring-size-property')?.closest('.shopify-block') || productGrid.querySelector('.amada-ring-size-property');
      const engravingProperty = productGrid.querySelector('product-custom-property-component');
      const productTitle = document.createElement('a');
      productTitle.textContent = productTitleText;
      productTitle.href = productUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      const firstImage = productGrid.querySelector('.product-media img, slideshow-slide img');
      if (firstImage) {
        const thumbWrapper = document.createElement('div');
        thumbWrapper.classList.add('product-header__thumb');
        const thumb = document.createElement('img');
        thumb.src = firstImage.getAttribute('src') || firstImage.src || '';
        thumb.alt = productTitleText;
        thumbWrapper.appendChild(thumb);
        productHeader.appendChild(thumbWrapper);
      }

      const textWrapper = document.createElement('div');
      textWrapper.classList.add('product-header__text');
      textWrapper.appendChild(productTitle);
      if (productPrice) {
        textWrapper.appendChild(productPrice);
      }
      productHeader.appendChild(textWrapper);

      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (ringSizeProperty) {
        productGrid.appendChild(ringSizeProperty);
      }
      if (engravingProperty) {
        productGrid.appendChild(engravingProperty);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productGrid.appendChild(createAssistanceElement(productTitleText, productUrl));

      productDetails?.remove();
      productMedia?.remove();
    } else {
      stripUnwantedQuickAddElements(productGrid);
      buildQuickAddThumbnails(productGrid);
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    // Remove any save this piece button and stray checkout buttons
    stripUnwantedQuickAddElements(modalContent);

    // Attach Buy Now button and place assistance card right below the buy buttons
    attachQuickAddBuyNowButton(modalContent);
    bindQuickAddLineItemFields(modalContent);
    attachQuickAddAssistance(modalContent, productTitleText, productUrl);

    this.#syncVariantSelection(modalContent);
    syncQuickAddEnquiry(modalContent);

    if (!isMobileBreakpoint()) {
      initQuickAddThumbnailInteractions(modalContent);
      highlightActiveThumbnail(modalContent);
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        syncQuickAddVariantId(this, detail);
        syncQuickAddEnquiry(this, detail);

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (viewMoreDetailsLink && anchorElement) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle && anchorElement) mobileProductTitle.href = anchorElement.href;

        const newImg = html.querySelector('.product-media img, slideshow-slide img');
        const mobileThumb = /** @type {HTMLImageElement | null} */ (this.querySelector('.product-header__thumb img'));
        if (newImg && mobileThumb) {
          const newSrc = newImg.getAttribute('src') || newImg.src;
          if (newSrc) mobileThumb.src = newSrc;
        }

        requestAnimationFrame(() => {
          stripUnwantedQuickAddElements(this);
          syncQuickAddVariantId(this, detail);
          attachQuickAddBuyNowButton(this);
          bindQuickAddLineItemFields(this);
          const currentTitle =
            this.querySelector('.amada-product-title, .product-header a, .view-product-title a')?.textContent?.trim() || '';
          const currentUrl = this.querySelector('.view-product-title a, .product-header a')?.href || this.productPageUrl || '';
          attachQuickAddAssistance(this, currentTitle, currentUrl);

          const slideshow = this.querySelector('slideshow-component');
          if (slideshow && !slideshow.querySelector('.quick-add-thumbnail-nav')) {
            buildQuickAddThumbnails(this);
            initQuickAddThumbnailInteractions(this);
          }
          highlightActiveThumbnail(this);
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
